import { apiTimeTracking } from "@/api/time-tracking.api.ts";
import { filtersLibrary, periodsLibrary } from "@/components/filter/filter.types.ts";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useTimesheets } from "@/queries/use-time-tracking.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import {
  ApiTimesheetEmployeeScope,
  ApiTimesheetExportLayout,
  ApiTimesheetFilter,
} from "@/types/time-tracking.types.ts";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  SplitButton,
  Stack,
  useDebouncedSearchParam,
} from "@skailer-com/uikit";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { TimesheetsTable } from "./components/TimesheetsTable.tsx";

export const TimesheetsPage = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { canIEdit } = usePermissions();
  const { downloadFile, isExporting } = useDownloadHelper();

  useTitle(t("clocking:timesheets"));

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.timeTracking.timesheetViewed);
  });

  const library = filtersLibrary();
  const periodLibrary = periodsLibrary();

  const {
    filter: { filter },
    config,
  } = useAppFilter<ApiTimesheetFilter>({
    filters: [
      library.page_number,
      library.text,
      { ...library.date_from, hideInFilter: true, name: "date_period_start" },
      { ...library.date_to, hideInFilter: true, name: "date_period_end" },
      { ...library.period, defaultValue: "last_week_0" },
      {
        ...library.text,
        name: "employee_scope",
        searchParamName: "scope",
        defaultValue: "active",
        hideInFilter: true,
      },
    ],
    source: "Timesheets",
  });

  const selectedScope = filter.employee_scope ?? ApiTimesheetEmployeeScope.active;

  const { value: searchValue, setValue: setSearchValue } = useDebouncedSearchParam({
    paramName: "search",
    delay: 300,
  });

  const { data, isLoading } = useTimesheets(filter);
  const periodDates = useMemo(
    () =>
      [...(data?.items[0]?.days ?? [])]
        .sort((a, b) => a.date_day.getTime() - b.date_day.getTime())
        .map((d) => d.date_day),
    [data]
  );

  const scopeOptions: { key: ApiTimesheetEmployeeScope; label: string }[] = [
    { key: ApiTimesheetEmployeeScope.active, label: t("clocking:scope_active") },
    { key: ApiTimesheetEmployeeScope.with_entries, label: t("clocking:scope_with_entries") },
    { key: ApiTimesheetEmployeeScope.all, label: t("clocking:scope_all") },
  ];

  const selectedScopeLabel = scopeOptions.find((o) => o.key === selectedScope)?.label;

  const handleExportClick = useCallback(
    (layout: ApiTimesheetExportLayout) => {
      if (isExporting) return;

      downloadFile({
        apiCall: () =>
          apiTimeTracking.exportTimesheets({
            ...filter,
            format: ApiExportFormat.xlsx,
            layout,
          }),
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.dataExported, {
            [analyticProperties.title]: "Timesheets",
            [analyticProperties.mode]: layout,
          });
        },
      });
    },
    [filter, downloadFile, isExporting]
  );

  return (
    <Page className="h-full">
      <Page.Header title={t("clocking:timesheets")}>
        <Stack direction={"horizontal"} gap={"sm"} className={"items-center"}>
          <FilterChoosePeriod
            filtersConfig={config}
            staticPeriods={[
              periodLibrary.last_week_0,
              periodLibrary.last_week_2,
              periodLibrary.last_week_4,
            ]}
          />
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {selectedScopeLabel}
              </Button>
            }
          >
            {scopeOptions.map((option) => (
              <DropdownMenuItem
                key={option.key}
                title={option.label}
                isSelected={selectedScope === option.key}
                onClick={() => {
                  setSearchParams((params) => {
                    params.set("scope", option.key);
                    return params;
                  });
                }}
              />
            ))}
          </DropdownMenu>
          {canIEdit(ApiPermission.reporting_export) && (
            <SplitButton
              isLoading={isExporting}
              tooltip={t("clocking:export_summary")}
              onClick={() => handleExportClick(ApiTimesheetExportLayout.summary)}
              menuLabel={t("common:more_export_options")}
              menu={
                <DropdownMenuItem
                  title={t("clocking:export_detailed")}
                  onClick={() => handleExportClick(ApiTimesheetExportLayout.detailed)}
                />
              }
            >
              {t("common:export")}
            </SplitButton>
          )}
        </Stack>
      </Page.Header>

      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <Search
              value={searchValue}
              onInput={(e) => setSearchValue(e.currentTarget.value)}
              placeholder={t("common:search_by_name")}
            />
          }
          footer={
            <PaginationBlock
              metadata={data?.metadata}
              onPageChange={(page) => {
                setSearchParams((p) => {
                  p.set("page", page.toString());
                  return p;
                });
              }}
            />
          }
        >
          <TimesheetsTable rows={data?.items ?? []} dates={periodDates} isLoading={isLoading} />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
};
